import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { LOGO_URL } from "@/lib/constants";
import { Search, User, Menu, X, Sun, Moon, Instagram } from "lucide-react";

const IG_URL = "https://www.instagram.com/cryptolyfe.xyz?igsh=OHZ1OHgyc2lvdTJh";
const TT_URL = "https://www.tiktok.com/@cryptolyfe.xyz?_r=1&_t=ZS-991iOWOpyDI";

function TikTokIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.6 5.8a4.3 4.3 0 0 1-2.6-3.8h-2.9v11.4a2.4 2.4 0 1 1-2.4-2.4c.2 0 .4 0 .6.1V8.1a5.3 5.3 0 1 0 4.7 5.3V8.3a7.2 7.2 0 0 0 4.1 1.3V6.7a4.3 4.3 0 0 1-1.5-.9z"/>
    </svg>
  );
}

const NAV = [
  { label: "News", to: "/news" },
  { label: "Research", to: "/research" },
  { label: "Conviction", to: "/conviction" },
  { label: "Analysts", to: "/analysts" },
  { label: "Calendar", to: "/calendar" },
  { label: "Membership", to: "/pricing" },
];

export function Navbar() {
  const { user, logout, isAlpha, isStaff, isEditor, isAnalyst } = useAuth();
  const { theme, toggle } = useTheme();
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  const active = (to) => location.pathname === to;

  return (
    <header className="sticky top-0 z-50 glass">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <img src={LOGO_URL} alt="OnchainLyfe" className="h-7 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                  active(n.to) ? "text-[#ccff00]" : "text-zinc-300 hover:text-white"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* socials */}
            <a href={IG_URL} target="_blank" rel="noreferrer" data-testid="social-instagram" title="Instagram"
              className="hidden sm:grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
            <a href={TT_URL} target="_blank" rel="noreferrer" data-testid="social-tiktok" title="TikTok"
              className="hidden sm:grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors">
              <TikTokIcon className="w-4 h-4" />
            </a>

            <div className="hidden md:flex items-center rounded-full border border-white/10 p-0.5 bg-black/40">
              {["en", "id"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-mono-cl tracking-[0.12em] uppercase transition-colors ${
                    lang === l ? "bg-[#ccff00] text-black font-bold" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <button
              data-testid="theme-toggle"
              onClick={toggle}
              className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors"
              title="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            <button className="hidden sm:grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:text-white hover:border-white/30 transition-colors">
              <Search className="w-4 h-4" />
            </button>

            {user ? (
              <div className="relative">
                <button
                  data-testid="user-menu-btn"
                  onClick={() => setUserMenu((v) => !v)}
                  className="grid place-items-center w-9 h-9 rounded-full border border-[#ccff00]/40 text-[#ccff00] hover:bg-[#ccff00]/10 transition-colors"
                >
                  <User className="w-4 h-4" />
                </button>
                {userMenu && (
                  <div
                    className="absolute right-0 mt-2 w-56 glass rounded-xl p-2 z-50"
                    onMouseLeave={() => setUserMenu(false)}
                  >
                    <div className="px-3 py-2 border-b border-white/10 mb-1">
                      <p className="text-sm font-semibold truncate">{user.name}</p>
                      {user.nickname && <p className="text-xs text-zinc-500 truncate">@{user.nickname}</p>}
                      <p className="text-[11px] font-mono-cl tracking-[0.1em] uppercase text-[#ccff00]">
                        {user.role === "admin" ? "Admin" : user.tier} tier
                      </p>
                    </div>
                    <button
                      data-testid="nav-account"
                      onClick={() => { setUserMenu(false); navigate("/account"); }}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/5"
                    >
                      Account & Wallets
                    </button>
                    {isEditor && (
                      <button
                        data-testid="nav-admin"
                        onClick={() => { setUserMenu(false); navigate("/admin"); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/5"
                      >
                        {isAnalyst ? "Analyst Studio" : user.role === "admin" ? "Admin CMS" : "Publisher CMS"}
                      </button>
                    )}
                    {isAnalyst && (
                      <button
                        data-testid="nav-my-profile"
                        onClick={() => { setUserMenu(false); navigate("/admin?tab=profile"); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/5"
                      >
                        My Public Profile
                      </button>
                    )}
                    {!isAlpha && (
                      <button
                        onClick={() => { setUserMenu(false); navigate("/pricing"); }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-[#ccff00] hover:bg-[#ccff00]/10"
                      >
                        Upgrade to Alpha
                      </button>
                    )}
                    <button
                      data-testid="logout-btn"
                      onClick={() => { setUserMenu(false); logout(); navigate("/"); }}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/5"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="hidden sm:block px-4 py-2 text-sm font-medium text-zinc-200 hover:text-white transition-colors"
                >
                  Log in
                </Link>
                <Link
                  to="/pricing"
                  data-testid="nav-join-alpha"
                  className="px-4 py-2 rounded-full bg-[#ccff00] text-black text-sm font-semibold hover:brightness-110 transition-[filter]"
                >
                  Join Alpha
                </Link>
              </>
            )}

            <button
              className="lg:hidden grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-200"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden glass border-t border-white/10 px-4 py-3">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setMenuOpen(false)}
              className="block px-2 py-2.5 text-sm text-zinc-200 hover:text-[#ccff00] border-b border-white/5"
            >
              {n.label}
            </Link>
          ))}
          {!user && (
            <Link to="/login" onClick={() => setMenuOpen(false)} className="block px-2 py-2.5 text-sm text-[#ccff00]">
              Log in
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
